/**
 * Pinia store for the Partner page (T06).
 *
 * Holds three pieces of state:
 *   - `summary`       — `PartnerSummary` from `GET /api/partner/me`
 *   - `referrals`     — current page of `ReferralRow[]` from
 *                       `GET /api/partner/referrals?page=&page_size=`
 *   - paging cursors  — `page`, `pageSize`, server-reported `total`
 *
 * The summary and the referral list are fetched independently so a slow
 * referrals query never blocks the hero block's referral code from rendering.
 *
 * Errors are kept as a single string for the view to surface — the page
 * doesn't need per-call error granularity for the MVP.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  partnerApi,
  type ListReferralsResponse,
  type PartnerSummary,
  type ReferralRow,
} from '../api/partner';

const DEFAULT_PAGE_SIZE = 25;

export const usePartnerStore = defineStore('partner', () => {
  const summary = ref<PartnerSummary | null>(null);
  const referrals = ref<ReferralRow[]>([]);
  const total = ref<number>(0);
  const page = ref<number>(1);
  const pageSize = ref<number>(DEFAULT_PAGE_SIZE);

  const loadingSummary = ref<boolean>(false);
  const loadingList = ref<boolean>(false);
  const error = ref<string | null>(null);

  /** Derived: number of pages (>= 1 when there's at least one row). */
  const totalPages = computed<number>(() => {
    if (total.value <= 0) return 1;
    return Math.max(1, Math.ceil(total.value / pageSize.value));
  });

  async function loadSummary(): Promise<void> {
    loadingSummary.value = true;
    error.value = null;
    try {
      summary.value = await partnerApi.me();
    } catch (err) {
      error.value = (err as Error).message;
    } finally {
      loadingSummary.value = false;
    }
  }

  async function loadReferrals(targetPage: number = page.value): Promise<void> {
    loadingList.value = true;
    error.value = null;
    try {
      const res: ListReferralsResponse = await partnerApi.referrals({
        page: targetPage,
        page_size: pageSize.value,
      });
      referrals.value = res.rows;
      total.value = res.total;
      page.value = targetPage;
    } catch (err) {
      error.value = (err as Error).message;
    } finally {
      loadingList.value = false;
    }
  }

  /** Convenience: hero + first page in parallel. Used by the view on mount. */
  async function refresh(): Promise<void> {
    await Promise.all([loadSummary(), loadReferrals(1)]);
  }

  function nextPage(): Promise<void> {
    if (page.value >= totalPages.value) return Promise.resolve();
    return loadReferrals(page.value + 1);
  }

  function prevPage(): Promise<void> {
    if (page.value <= 1) return Promise.resolve();
    return loadReferrals(page.value - 1);
  }

  return {
    summary,
    referrals,
    total,
    page,
    pageSize,
    totalPages,
    loadingSummary,
    loadingList,
    error,
    loadSummary,
    loadReferrals,
    refresh,
    nextPage,
    prevPage,
  };
});
